import { createClient, withApiBaseUrl } from '@repo/http-client';
// Load before API modules so bundled output keeps BaseApi ahead of `extends BaseApi` classes.
import { BaseApi } from './api/base.api.js';
import { ApiError } from './errors/api-error.js';

// Import all APIs
import { ApplicationMethodApi } from './api/application-method.api.js';
import { ApplicationRequestInformationApi } from './api/application-request-information.api.js';
import { ApplicationTrackingApi } from './api/application-tracking.api.js';
import { AssessmentQuestionApi } from './api/assessment-question.api.js';
import { AssessmentApi } from './api/assessment.api.js';
import { AssetCategoryApi } from './api/asset-category.api.js';
import { AssetGroupCriticalityEvaluationApi } from './api/asset-group-criticality-evaluation.api.js';
import { AssetGroupMeasureApplicabilityApi } from './api/asset-group-measure-applicability.api.js';
import { AssetGroupApi } from './api/asset-group.api.js';
import { AssetApi } from './api/asset.api.js';
import { AuditAssignmentApi } from './api/audit-assignment.api.js';
import { AuditFindingApi } from './api/audit-finding.api.js';
import { AuditPlanApi } from './api/audit-plan.api.js';
import { AuthApi } from './api/auth.api.js';
import { RootCauseApi } from './api/root-cause.api.js';
import { BcmInitialResponseLibraryApi } from './api/bcm-initial-response-library.api.js';
import { BcmRpoApi } from './api/bcm-rpo.api.js';
import { BcmSettingsApi } from './api/bcm-settings.api.js';
import { BcpApi } from './api/bcp.api.js';
import { BiaApi } from './api/bia.api.js';
import { CollectionMethodApi } from './api/collection-method.api.js';
import { CommunicationApi } from './api/communication.api.js';
import { CommunicationPlanApi } from './api/communication-plan.api.js';
import { CompanyApi } from './api/company.api.js';
import { CompetenceApi } from './api/competence.api.js';
import { ClassificationApi } from './api/classification.api.js';
import { ConsentRecordApi } from './api/consent-record.api.js';
import { PrivacyNoticeApi } from './api/privacy-notice.api.js';
import { DpiaAssessmentApi } from './api/dpia-assessment.api.js';
import { DisposalApi } from './api/disposal.api.js';
import { VerbisApi } from './api/verbis.api.js';
import { GdprDashboardApi } from './api/gdpr-dashboard.api.js';
import { CookieInventoryApi } from './api/cookie-inventory.api.js';
import { DataProcessingAgreementApi } from './api/data-processing-agreement.api.js';
import { ContextApi } from './api/context.api.js';
import { InterestedPartyApi } from './api/interested-party.api.js';
import { ScopeDocumentApi } from './api/scope-document.api.js';
import { ServiceCatalogItemApi } from './api/service-catalog-item.api.js';
import { ChangeRequestApi } from './api/change-request.api.js';
import { ProblemApi } from './api/problem.api.js';
import { VulnerabilityApi } from './api/vulnerability.api.js';
import { ControlApi } from './api/control.api.js';
import { CorrectiveActionApi } from './api/corrective-action.api.js';
import { CrisisEventApi } from './api/crisis-event.api.js';
import { CrisisRecoveryActivitiesApi } from './api/crisis-recovery-activities.api.js';
import { CrisisRecoveryActivityApi } from './api/crisis-recovery-activity.api.js';
import { DashboardApi } from './api/dashboard.api.js';
import { DashboardLayoutApi } from './api/dashboard-layout.api.js';
import { HomeApi } from './api/home.api.js';
import { DataBreachApi } from './api/data-breach.api.js';
import { DataDisposalMethodApi } from './api/data-disposal-method.api.js';
import { BigdesMeasureApi } from './api/bigdes-measure.api.js';
import { BigdesRoadMapWorkPackageApi } from './api/bigdes-road-map-work-package.api.js';
import { BigdesCompensatoryControlApi } from './api/bigdes-compensatory-control.api.js';
import { BigdesImplementationProcessEvaluationApi } from './api/bigdes-implementation-process-evaluation.api.js';
import { BigdesCurrentSituationEvaluationScopeApi } from './api/bigdes-current-situation-evaluation-scope.api.js';
import { ReportArchiveApi } from './api/report-archive.api.js';
import { ReportingArchiveApi } from './api/reporting-archive.api.js';
import { DataSourceApi } from './api/data-source.api.js';
import { DataSubjectRequestApi } from './api/data-subject-request.api.js';
import { UnitApi } from './api/unit.api.js';
import { DocumentTypeApi } from './api/document-type.api.js';
import { DocumentApi } from './api/document.api.js';
import { DocumentRevisionApi } from './api/document-revision.api.js';
import { AssignmentTemplateApi } from './api/assignment-template.api.js';
import { RpoCalculationApi } from './api/rpo-calculation.api.js';
import { RpoRecommendationApi } from './api/rpo-recommendation.api.js';

import { MatrixConfigurationApi } from './api/matrix-configuration.api.js';
import { BiaImpactConfigurationApi } from './api/bia-impact-configuration.api.js';
import { ExerciseApi } from './api/exercise.api.js';
import { ExerciseParticipantApi } from './api/exercise-participant.api.js';
import { ScenarioTimelineApi } from './api/scenario-timeline.api.js';
import { ExerciseRecoveryActivityApi } from './api/exercise-recovery-activity.api.js';
import { ExerciseEvaluationApi } from './api/exercise-evaluation.api.js';
import { OperationalRunApi } from './api/operational-run.api.js';
import { ExplicitConsentMethodApi } from './api/explicit-consent-method.api.js';
import { ExplicitConsentReasonApi } from './api/explicit-consent-reason.api.js';
import { ExplicitConsentApi } from './api/explicit-consent.api.js';
import { FormSubmissionApi } from './api/form-submission.api.js';
import { FormTemplateApi } from './api/form-template.api.js';
import { FormTaskApi } from './api/form-task.api.js';
import { SurveyDispatchApi } from './api/survey-dispatch.api.js';
import { GapApi } from './api/gap.api.js';
import { IncidentApi } from './api/incident.api.js';
import { IncidentTemplateApi } from './api/incident-template.api.js';
import { KpiApi } from './api/kpi.api.js';
import { LifecycleApi } from './api/lifecycle.api.js';
import { KvkkCompanyApi } from './api/kvkk-company.api.js';
import { LegalReasonApi } from './api/legal-reason.api.js';
import { MailTemplateApi } from './api/mail-template.api.js';
import { CommunicationChannelApi } from './api/communication-channel.api.js';
import { CommunicationProviderApi } from './api/communication-provider.api.js';
import { TenantMfaSettingsApi } from './api/tenant-mfa-settings.api.js';
import { SeedPolicyApi } from './api/seed-policy.api.js';
import { IntegrationConnectionApi } from './api/integration-connection.api.js';
import { MeetingApi } from './api/meeting.api.js';
import { NotificationApi } from './api/notification.api.js';
import { NotificationPreferenceApi } from './api/notification-preference.api.js';
import { UserNavigationPreferenceApi } from './api/user-navigation-preference.api.js';
import { NotificationTemplateApi } from './api/notification-template.api.js';
import { NotificationRoutingApi } from './api/notification-routing.api.js';
import { ObjectiveApi } from './api/objective.api.js';
// PageApprovalConfig / PendingApproval / PendingChange APIs removed (Plan Faz 1.7).
import { PartyTemplateApi } from './api/party-template.api.js';
import { PartyInstanceApi } from './api/party-instance.api.js';
import { WorkflowTemplateApi } from './api/workflow-template.api.js';
import { WorkflowInstanceApi } from './api/workflow-instance.api.js';
import { WorkflowTaskApi } from './api/workflow-task.api.js';
import { BusinessCalendarApi } from './api/business-calendar.api.js';
import { EntityEventApi } from './api/entity-event.api.js';
import { DelegationApi } from './api/delegation.api.js';
import { DeadLetterApi } from './api/dead-letter.api.js';
import { BackupApi } from './api/backup.api.js';
import { TailorabilityApi } from './api/tailorability.api.js';
import { WebhookSubscriptionApi } from './api/webhook-subscription.api.js';
import { TenantComplianceApi } from './api/tenant-compliance.api.js';
import { IntegrationExportApi } from './api/integration-export.api.js';
import { CustomerImportApi } from './api/customer-import.api.js';
import { TenantOnboardingApi } from './api/tenant-onboarding.api.js';
import { MyWorkApi } from './api/my-work.api.js';
import { PersonalDataCategoryApi } from './api/personal-data-category.api.js';
import { PersonalDataInventoryApi } from './api/personal-data-inventory.api.js';
import { PrivacyPrincipleApi } from './api/privacy-principle.api.js';
import { ProcessApi } from './api/process.api.js';
import { ProcessingPurposeApi } from './api/processing-purpose.api.js';
import { QueueObservabilityApi } from './api/queue-observability.api.js';
import { OutboxMonitorApi } from './api/outbox-monitor.api.js';
import { EventObservabilityApi } from './api/event-observability.api.js';
import { RecipientGroupApi } from './api/recipient-group.api.js';
import { RecoveryResourcesApi } from './api/recovery-resources.api.js';
import { LessonLearnedApi } from './api/lesson-learned.api.js';
import { DetailedReportProfileApi } from './api/detailed-report-profile.api.js';
import { ReportBrandingApi } from './api/report-branding.api.js';
import { ReportArtifactApi } from './api/report-artifact.api.js';
import { ReportProfileApi } from './api/report-profile.api.js';
import { DetailedReportingApi } from './api/detailed-reporting.api.js';
import { ReportSurfacesApi } from './api/report-surfaces.api.js';
import { ReportRunsApi } from './api/report-runs.api.js';
import { RetentionPeriodApi } from './api/retention-period.api.js';
import { RiskAnalysisApi } from './api/risk-analysis.api.js';
import { RiskRegisterApi } from './api/risk-register.api.js';
import { RiskConsequenceApi } from './api/risk-consequence.api.js';
import { RiskReportsApi } from './api/risk-reports.api.js';
import { RiskTreatmentApi } from './api/risk-treatment.api.js';
import { RiskApi } from './api/risk.api.js';
import { RoleApi } from './api/role.api.js';
import { SecurityMeasureApi } from './api/security-measure.api.js';
import { StrategicInitiativeApi } from './api/strategic-initiative.api.js';
import { SubjectPersonGroupApi } from './api/subject-person-group.api.js';
import { SystemLogsApi } from './api/system-logs.api.js';
import { EvidenceBundleApi } from './api/evidence-bundle.api.js';
import { AccessLogsApi } from './api/access-logs.api.js';
import { TeamApi } from './api/team.api.js';
import { TenantDirectoryApi } from './api/tenant-directory.api.js';
import { IdentityProviderApi } from './api/identity-provider.api.js';
import { ThreatApi } from './api/threat.api.js';
import { TrainingApi } from './api/training.api.js';
import { TransferTypeApi } from './api/transfer-type.api.js';
import { InternationalTransferApi } from './api/international-transfer.api.js';
import { UserTrainingApi } from './api/user-training.api.js';
import { UserApi } from './api/user.api.js';
import { VendorApi } from './api/vendor.api.js';
import { WeaknessApi } from './api/weakness.api.js';
import { ReferenceDataApi } from './api/reference-data.api.js';
import { RegulatorySourceApi } from './api/regulatory-source.api.js';
import { RegulatoryClauseApi } from './api/regulatory-clause.api.js';
import { ComplianceObligationApi } from './api/compliance-obligation.api.js';
import { ComplianceFrameworkApi } from './api/compliance-framework.api.js';
import { RiskMitigationDefinitionApi } from './api/risk-mitigation-definition.api.js';
import { DpiaTemplateApi } from './api/dpia-template.api.js';
import { VendorAssessmentApi } from './api/vendor-assessment.api.js';
import { IntegrationConnectorApi } from './api/integration-connector.api.js';
import { GovernanceReportDefinitionApi } from './api/governance-report-definition.api.js';
// AutomationDefinition API removed (Plan Faz 1.7). Replaced by WorkflowTemplate API (Plan Faz 5, ADR-006).

// Import all Services
import { ApplicationMethodService } from './services/application-method.service.js';
import { ApplicationRequestInformationService } from './services/application-request-information.service.js';
import { ApplicationTrackingService } from './services/application-tracking.service.js';
import { AssessmentQuestionService } from './services/assessment-question.service.js';
import { AssessmentService } from './services/assessment.service.js';
import { AssetCategoryService } from './services/asset-category.service.js';
import { AssetGroupCriticalityEvaluationService } from './services/asset-group-criticality-evaluation.service.js';
import { AssetGroupMeasureApplicabilityService } from './services/asset-group-measure-applicability.service.js';
import { AssetGroupService } from './services/asset-group.service.js';
import { AssetService } from './services/asset.service.js';
import { AuditAssignmentService } from './services/audit-assignment.service.js';
import { AuditFindingService } from './services/audit-finding.service.js';
import { AuditPlanService } from './services/audit-plan.service.js';
import { AuthService } from './services/auth.service.js';
import { RootCauseService } from './services/root-cause.service.js';
import { BcmInitialResponseLibraryService } from './services/bcm-initial-response-library.service.js';
import { BcmRpoService } from './services/bcm-rpo.service.js';
import { BcmSettingsService } from './services/bcm-settings.service.js';
import { BcpService } from './services/bcp.service.js';
import { BiaService } from './services/bia.service.js';
import { CollectionMethodService } from './services/collection-method.service.js';
import { CommunicationService } from './services/communication.service.js';
import { CommunicationPlanService } from './services/communication-plan.service.js';
import { CompanyService } from './services/company.service.js';
import { CompetenceService } from './services/competence.service.js';
import { ClassificationService } from './services/classification.service.js';
import { ConsentRecordService } from './services/consent-record.service.js';
import { PrivacyNoticeService } from './services/privacy-notice.service.js';
import { DpiaAssessmentService } from './services/dpia-assessment.service.js';
import { DisposalService } from './services/disposal.service.js';
import { VerbisService } from './services/verbis.service.js';
import { GdprDashboardService } from './services/gdpr-dashboard.service.js';
import { CookieInventoryService } from './services/cookie-inventory.service.js';
import { DataProcessingAgreementService } from './services/data-processing-agreement.service.js';
import { ContextService } from './services/context.service.js';
import { InterestedPartyService } from './services/interested-party.service.js';
import { ScopeDocumentService } from './services/scope-document.service.js';
import { ServiceCatalogItemService } from './services/service-catalog-item.service.js';
import { ChangeRequestService } from './services/change-request.service.js';
import { ProblemService } from './services/problem.service.js';
import { VulnerabilityService } from './services/vulnerability.service.js';
import { ControlService } from './services/control.service.js';
import { CorrectiveActionService } from './services/corrective-action.service.js';
import { CrisisEventService } from './services/crisis-event.service.js';
import { CrisisRecoveryActivitiesService } from './services/crisis-recovery-activities.service.js';
import { CrisisRecoveryActivityService } from './services/crisis-recovery-activity.service.js';
import { DashboardService } from './services/dashboard.service.js';
import { DashboardLayoutService } from './services/dashboard-layout.service.js';
import { HomeService } from './services/home.service.js';
import { DataBreachService } from './services/data-breach.service.js';
import { DataDisposalMethodService } from './services/data-disposal-method.service.js';
import { BigdesMeasureService } from './services/bigdes-measure.service.js';
import { BigdesRoadMapWorkPackageService } from './services/bigdes-road-map-work-package.service.js';
import { BigdesCompensatoryControlService } from './services/bigdes-compensatory-control.service.js';
import { BigdesImplementationProcessEvaluationService } from './services/bigdes-implementation-process-evaluation.service.js';
import { BigdesCurrentSituationEvaluationScopeService } from './services/bigdes-current-situation-evaluation-scope.service.js';
import { ReportArchiveService } from './services/report-archive.service.js';
import { ReportingArchiveService } from './services/reporting-archive.service.js';
import { DataSourceService } from './services/data-source.service.js';
import { DataSubjectRequestService } from './services/data-subject-request.service.js';
import { UnitService } from './services/unit.service.js';
import { DocumentTypeService } from './services/document-type.service.js';
import { DocumentService } from './services/document.service.js';
import { DocumentRevisionService } from './services/document-revision.service.js';
import { AssignmentTemplateService } from './services/assignment-template.service.js';

import { MatrixConfigurationService } from './services/matrix-configuration.service.js';
import { BiaImpactConfigurationService } from './services/bia-impact-configuration.service.js';
import { ExercisesService } from './services/exercises.service.js';
import { ExerciseParticipantService } from './services/exercise-participant.service.js';
import { ScenarioTimelineService } from './services/scenario-timeline.service.js';
import { ExerciseRecoveryActivityService } from './services/exercise-recovery-activity.service.js';
import { ExerciseEvaluationService } from './services/exercise-evaluation.service.js';
import { OperationalRunService } from './services/operational-run.service.js';
import { ExplicitConsentMethodService } from './services/explicit-consent-method.service.js';
import { ExplicitConsentReasonService } from './services/explicit-consent-reason.service.js';
import { ExplicitConsentService } from './services/explicit-consent.service.js';
import { FormSubmissionService } from './services/form-submission.service.js';
import { FormTemplateService } from './services/form-template.service.js';
import { FormTaskService } from './services/form-task.service.js';
import { SurveyDispatchService } from './services/survey-dispatch.service.js';
import { CollectionCampaignApi } from './api/collection-campaign.api.js';
import { CollectionCampaignService } from './services/collection-campaign.service.js';
import { GapService } from './services/gap.service.js';
import { IncidentService } from './services/incident.service.js';
import { KpiService } from './services/kpi.service.js';
import { LifecycleService } from './services/lifecycle.service.js';
import { KvkkCompanyService } from './services/kvkk-company.service.js';
import { LegalReasonService } from './services/legal-reason.service.js';
import { MailTemplateService } from './services/mail-template.service.js';
import { CommunicationChannelService } from './services/communication-channel.service.js';
import { CommunicationProviderService } from './services/communication-provider.service.js';
import { TenantMfaSettingsService } from './services/tenant-mfa-settings.service.js';
import { SeedPolicyService } from './services/seed-policy.service.js';
import { IntegrationConnectionService } from './services/integration-connection.service.js';
import { MeetingService } from './services/meeting.service.js';
import { NotificationService } from './services/notification.service.js';
import { NotificationPreferenceService } from './services/notification-preference.service.js';
import { UserNavigationPreferenceService } from './services/user-navigation-preference.service.js';
import { NotificationTemplateService } from './services/notification-template.service.js';
import { NotificationRoutingService } from './services/notification-routing.service.js';
import { ObjectiveService } from './services/objective.service.js';
// PageApprovalConfig / PendingApproval / PendingChange services removed (Plan Faz 1.7).
import { PartyTemplateService } from './services/party-template.service.js';
import { PartyInstanceService } from './services/party-instance.service.js';
import { WorkflowTemplateService } from './services/workflow-template.service.js';
import { WorkflowInstanceService } from './services/workflow-instance.service.js';
import { WorkflowTaskService } from './services/workflow-task.service.js';
import { BusinessCalendarService } from './services/business-calendar.service.js';
import { EntityEventService } from './services/entity-event.service.js';
import { DelegationService } from './services/delegation.service.js';
import { DeadLetterService } from './services/dead-letter.service.js';
import { BackupService } from './services/backup.service.js';
import { TailorabilityService } from './services/tailorability.service.js';
import { WebhookSubscriptionService } from './services/webhook-subscription.service.js';
import { TenantComplianceService } from './services/tenant-compliance.service.js';
import { IntegrationExportService } from './services/integration-export.service.js';
import { CustomerImportService } from './services/customer-import.service.js';
import { TenantOnboardingService } from './services/tenant-onboarding.service.js';
import { MyWorkService } from './services/my-work.service.js';
import { PersonalDataCategoryService } from './services/personal-data-category.service.js';
import { PersonalDataInventoryService } from './services/personal-data-inventory.service.js';
import { PrivacyPrincipleService } from './services/privacy-principle.service.js';
import { ProcessService } from './services/process.service.js';
import { QueueObservabilityService } from './services/queue-observability.service.js';
import { OutboxMonitorService } from './services/outbox-monitor.service.js';
import { EventObservabilityService } from './services/event-observability.service.js';
import { ProcessingPurposeService } from './services/processing-purpose.service.js';
import { RecipientGroupService } from './services/recipient-group.service.js';
import { RecoveryResourcesService } from './services/recovery-resources.service.js';
import { LessonLearnedService } from './services/lesson-learned.service.js';
import { DetailedReportProfileService } from './services/detailed-report-profile.service.js';
import { ReportBrandingService } from './services/report-branding.service.js';
import { ReportArtifactService } from './services/report-artifact.service.js';
import { ReportProfileService } from './services/report-profile.service.js';
import { DetailedReportingService } from './services/detailed-reporting.service.js';
import { ReportSurfacesService } from './services/report-surfaces.service.js';
import { ReportRunsService } from './services/report-runs.service.js';
import { RetentionPeriodService } from './services/retention-period.service.js';
import { RiskAnalysisService } from './services/risk-analysis.service.js';
import { RiskRegisterService } from './services/risk-register.service.js';
import { RiskConsequenceService } from './services/risk-consequence.service.js';
import { RiskReportsService } from './services/risk-reports.service.js';
import { RiskTreatmentService } from './services/risk-treatment.service.js';
import { RiskService } from './services/risk.service.js';
import { RoleService } from './services/role.service.js';
import { SecurityMeasureService } from './services/security-measure.service.js';
import { StrategicInitiativeService } from './services/strategic-initiative.service.js';
import { SubjectPersonGroupService } from './services/subject-person-group.service.js';
import { SystemLogsService } from './services/system-logs.service.js';
import { EvidenceBundleService } from './services/evidence-bundle.service.js';
import { AccessLogsService } from './services/access-logs.service.js';
import { TeamService } from './services/team.service.js';
import { TenantDirectoryService } from './services/tenant-directory.service.js';
import { IdentityProviderService } from './services/identity-provider.service.js';
import { ThreatService } from './services/threat.service.js';
import { TrainingService } from './services/training.service.js';
import { TransferTypeService } from './services/transfer-type.service.js';
import { InternationalTransferService } from './services/international-transfer.service.js';
import { UserTrainingService } from './services/user-training.service.js';
import { UserService } from './services/user.service.js';
import { VendorService } from './services/vendor.service.js';
import { WeaknessService } from './services/weakness.service.js';
import { ReferenceDataService } from './services/reference-data.service.js';
import { SimpleCrudService } from './services/simple-crud.service.js';
// AutomationDefinition service removed (Plan Faz 1.7). Replaced by WorkflowTemplate service (Plan Faz 5).
import { RpoCalculationService } from './services/rpo-calculation.service.js';
import { RpoRecommendationService } from './services/rpo-recommendation.service.js';

/**
 * [ENTERPRISE SDK CLIENT]
 */
export class APIClient {
    constructor(config = {}) {
        const {
            baseURL,
            timeout = 15000,
            tokenStore = this._getDefaultTokenStore(),
            enableCircuitBreaker = true,
            ...otherConfig
        } = config;

        this.config = { ...config, baseURL: withApiBaseUrl(baseURL) };
        this.tokenStore = tokenStore;

        this.client = createClient({
            baseURL: this.config.baseURL,
            timeout,
            name: 'EnterpriseSDK',
            enableCircuitBreaker,
            onAuthError: async () => this._handleRefresh(),
            ...otherConfig
        });

        this.client.interceptors.request.use(async (req) => {
            const token = await this.tokenStore.get();
            if (token) {
                req.headers.Authorization = 'Bearer ' + token;
            }
            return req;
        });

        // Initialize All Services
        this.applicationMethod = new ApplicationMethodService(new ApplicationMethodApi(this.client, '/application-method'), this.config);
        this.applicationRequestInformation = new ApplicationRequestInformationService(new ApplicationRequestInformationApi(this.client, '/application-request-information'), this.config);
        this.applicationTracking = new ApplicationTrackingService(new ApplicationTrackingApi(this.client, '/application-tracking'), this.config);
        this.assessmentQuestion = new AssessmentQuestionService(new AssessmentQuestionApi(this.client, '/assessment-question'), this.config);
        this.assessment = new AssessmentService(new AssessmentApi(this.client, '/assessment'), this.config);
        this.assetCategory = new AssetCategoryService(new AssetCategoryApi(this.client, '/asset-category'), this.config);
        this.assetGroupCriticalityEvaluation = new AssetGroupCriticalityEvaluationService(new AssetGroupCriticalityEvaluationApi(this.client), this.config);
        this.assetGroupMeasureApplicability = new AssetGroupMeasureApplicabilityService(new AssetGroupMeasureApplicabilityApi(this.client), this.config);
        this.assetGroup = new AssetGroupService(new AssetGroupApi(this.client, '/asset-group'), this.config);
        this.asset = new AssetService(new AssetApi(this.client, '/asset'), this.config);
        this.assetLicense = new SimpleCrudService(new BaseApi(this.client, '/asset-licenses'), { listMessage: 'Lisans kayıtları getirildi' });
        this.assetDisposal = new SimpleCrudService(new BaseApi(this.client, '/asset-disposals'), { listMessage: 'İmha kayıtları getirildi' });
        this.assetLifecycleEvent = new SimpleCrudService(new BaseApi(this.client, '/asset-lifecycle-events'), { listMessage: 'Yaşam döngüsü olayları getirildi' });
        this.auditAssignment = new AuditAssignmentService(new AuditAssignmentApi(this.client, '/audit-assignment'), this.config);
        this.auditFinding = new AuditFindingService(new AuditFindingApi(this.client, '/audit-finding'), this.config);
        this.auditPlan = new AuditPlanService(new AuditPlanApi(this.client, '/audit-plan'), this.config);
        this.auth = new AuthService(new AuthApi(this.client, '/auth'), this.config);
        this.rootCause = new RootCauseService(new RootCauseApi(this.client, '/root-cause'), this.config);
        this.bcmInitialResponseLibrary = new BcmInitialResponseLibraryService(new BcmInitialResponseLibraryApi(this.client, '/bcm-initial-response-library'), this.config);
        this.bcmRpo = new BcmRpoService(new BcmRpoApi(this.client, '/bcm-rpo'), this.config);
        this.bcmSettings = new BcmSettingsService(new BcmSettingsApi(this.client, '/bcm-settings'), this.config);
        this.bcp = new BcpService(new BcpApi(this.client, '/bcp'), this.config);
        this.bia = new BiaService(new BiaApi(this.client, '/bia'), this.config);
        this.biaImpactConfiguration = new BiaImpactConfigurationService(
            new BiaImpactConfigurationApi(this.client),
            this.config,
        );
        this.collectionMethod = new CollectionMethodService(new CollectionMethodApi(this.client, '/collection-method'), this.config);
        this.communication = new CommunicationService(new CommunicationApi(this.client, '/communication'), this.config);
        this.communicationPlan = new CommunicationPlanService(new CommunicationPlanApi(this.client, '/communication-plan'), this.config);
        this.company = new CompanyService(new CompanyApi(this.client, '/company'), this.config);
        this.competence = new CompetenceService(new CompetenceApi(this.client, '/competence'), this.config);
        this.classification = new ClassificationService(new ClassificationApi(this.client), this.config);
        this.consentRecord = new ConsentRecordService(new ConsentRecordApi(this.client, '/consent-record'), this.config);
        this.privacyNotice = new PrivacyNoticeService(new PrivacyNoticeApi(this.client), this.config);
        this.dpiaAssessment = new DpiaAssessmentService(new DpiaAssessmentApi(this.client), this.config);
        this.disposal = new DisposalService(new DisposalApi(this.client), this.config);
        this.verbis = new VerbisService(new VerbisApi(this.client), this.config);
        this.gdprDashboard = new GdprDashboardService(new GdprDashboardApi(this.client), this.config);
        this.cookieInventory = new CookieInventoryService(new CookieInventoryApi(this.client), this.config);
        this.dataProcessingAgreement = new DataProcessingAgreementService(new DataProcessingAgreementApi(this.client), this.config);
        this.context = new ContextService(new ContextApi(this.client, '/context'), this.config);
        this.interestedParty = new InterestedPartyService(new InterestedPartyApi(this.client, '/interested-party'), this.config);
        this.scopeDocument = new ScopeDocumentService(new ScopeDocumentApi(this.client, '/scope-document'), this.config);
        this.serviceCatalogItem = new ServiceCatalogItemService(new ServiceCatalogItemApi(this.client), this.config);
        this.changeRequest = new ChangeRequestService(new ChangeRequestApi(this.client), this.config);
        this.problem = new ProblemService(new ProblemApi(this.client), this.config);
        this.vulnerability = new VulnerabilityService(new VulnerabilityApi(this.client), this.config);
        this.control = new ControlService(new ControlApi(this.client, '/control'), this.config);
        this.correctiveAction = new CorrectiveActionService(new CorrectiveActionApi(this.client, '/corrective-action'), this.config);
        this.crisisEvent = new CrisisEventService(new CrisisEventApi(this.client, '/crisis-event'), this.config);
        this.crisisRecoveryActivities = new CrisisRecoveryActivitiesService(new CrisisRecoveryActivitiesApi(this.client, '/crisis-recovery-activities'), this.config);
        this.crisisRecoveryActivity = new CrisisRecoveryActivityService(new CrisisRecoveryActivityApi(this.client, '/crisis-recovery-activities'), this.config);
        this.dashboard = new DashboardService(new DashboardApi(this.client, '/dashboard'), this.config);
        this.dashboardLayout = new DashboardLayoutService(new DashboardLayoutApi(this.client), this.config);
        this.home = new HomeService(new HomeApi(this.client, '/home'), this.config);
        this.dataBreach = new DataBreachService(new DataBreachApi(this.client, '/data-breach'), this.config);
        this.dataDisposalMethod = new DataDisposalMethodService(new DataDisposalMethodApi(this.client, '/data-disposal-method'), this.config);
        this.bigdesMeasure = new BigdesMeasureService(new BigdesMeasureApi(this.client, '/bigdes-measure'), this.config);
        this.bigdesRoadMapWorkPackage = new BigdesRoadMapWorkPackageService(new BigdesRoadMapWorkPackageApi(this.client), this.config);
        this.bigdesCompensatoryControl = new BigdesCompensatoryControlService(new BigdesCompensatoryControlApi(this.client), this.config);
        this.bigdesImplementationProcessEvaluation = new BigdesImplementationProcessEvaluationService(
            new BigdesImplementationProcessEvaluationApi(this.client),
            this.config,
        );
        this.bigdesCurrentSituationEvaluationScope = new BigdesCurrentSituationEvaluationScopeService(
            new BigdesCurrentSituationEvaluationScopeApi(this.client),
            this.config,
        );
        this.reportArchive = new ReportArchiveService(new ReportArchiveApi(this.client), this.config);
        this.reportingArchive = new ReportingArchiveService(new ReportingArchiveApi(this.client));
        this.dataSource = new DataSourceService(new DataSourceApi(this.client, '/data-source'), this.config);
        this.dataSubjectRequest = new DataSubjectRequestService(new DataSubjectRequestApi(this.client, '/data-subject-request'), this.config);
        this.unit = new UnitService(new UnitApi(this.client, '/unit'), this.config);
        this.documentType = new DocumentTypeService(new DocumentTypeApi(this.client, '/document-type'), this.config);
        const documentRevisionApi = new DocumentRevisionApi(this.client, '/document');
        this.documentRevision = new DocumentRevisionService(documentRevisionApi, this.config);
        this.document = new DocumentService(new DocumentApi(this.client, '/document'), {
            ...this.config,
            documentRevisionApi,
        });
        this.assignmentTemplate = new AssignmentTemplateService(
            new AssignmentTemplateApi(this.client, '/assignment-templates'),
            this.config,
        );

        this.matrixConfiguration = new MatrixConfigurationService(new MatrixConfigurationApi(this.client), this.config);
        this.exercises = new ExercisesService(new ExerciseApi(this.client, '/exercises'), this.config);
        this.exerciseParticipant = new ExerciseParticipantService(new ExerciseParticipantApi(this.client), this.config);
        this.scenarioTimeline = new ScenarioTimelineService(new ScenarioTimelineApi(this.client), this.config);
        this.exerciseRecoveryActivity = new ExerciseRecoveryActivityService(new ExerciseRecoveryActivityApi(this.client), this.config);
        this.exerciseEvaluation = new ExerciseEvaluationService(new ExerciseEvaluationApi(this.client), this.config);
        this.operationalRun = new OperationalRunService(new OperationalRunApi(this.client));
        this.explicitConsentMethod = new ExplicitConsentMethodService(new ExplicitConsentMethodApi(this.client, '/explicit-consent-method'), this.config);
        this.explicitConsentReason = new ExplicitConsentReasonService(new ExplicitConsentReasonApi(this.client, '/explicit-consent-reason'), this.config);
        this.explicitConsent = new ExplicitConsentService(new ExplicitConsentApi(this.client, '/explicit-consent'), this.config);
        this.formSubmission = new FormSubmissionService(new FormSubmissionApi(this.client, '/form-submission'), this.config);
        this.formTemplate = new FormTemplateService(new FormTemplateApi(this.client, '/form-template'), this.config);
        this.formTask = new FormTaskService(new FormTaskApi(this.client, '/form-task'), this.config);
        this.surveyDispatch = new SurveyDispatchService(new SurveyDispatchApi(this.client, '/survey-dispatches'), this.config);
        this.collectionCampaign = new CollectionCampaignService(new CollectionCampaignApi(this.client, '/collection-campaigns'));
        this.gap = new GapService(new GapApi(this.client, '/gap'), this.config);
        this.incident = new IncidentService(new IncidentApi(this.client, '/incident'), this.config);
        this.kpi = new KpiService(new KpiApi(this.client, '/kpi'), this.config);
        this.lifecycle = new LifecycleService(new LifecycleApi(this.client), this.config);
        this.kvkkCompany = new KvkkCompanyService(new KvkkCompanyApi(this.client, '/kvkk-company'), this.config);
        this.legalReason = new LegalReasonService(new LegalReasonApi(this.client, '/legal-reason'), this.config);
        this.mailTemplate = new MailTemplateService(new MailTemplateApi(this.client, '/mail-template'), this.config);
        this.communicationChannel = new CommunicationChannelService(new CommunicationChannelApi(this.client, '/communication-channels'));
        this.communicationProvider = new CommunicationProviderService(new CommunicationProviderApi(this.client, '/communication-provider'), this.config);
        this.tenantMfaSettings = new TenantMfaSettingsService(new TenantMfaSettingsApi(this.client, '/tenant-mfa-settings'));
        this.seedPolicy = new SeedPolicyService(new SeedPolicyApi(this.client, '/seed-policy'));
        this.integrationConnection = new IntegrationConnectionService(new IntegrationConnectionApi(this.client, '/integration-connections'), this.config);
        this.meeting = new MeetingService(new MeetingApi(this.client, '/meeting'), this.config);
        this.notification = new NotificationService(new NotificationApi(this.client), this.config);
        this.notificationPreference = new NotificationPreferenceService(
            new NotificationPreferenceApi(this.client),
            this.config,
        );
        this.userNavigationPreference = new UserNavigationPreferenceService(
            new UserNavigationPreferenceApi(this.client),
            this.config,
        );
        this.notificationTemplate = new NotificationTemplateService(new NotificationTemplateApi(this.client, '/notification-template'));
        this.notificationRouting = new NotificationRoutingService(new NotificationRoutingApi(this.client));
        this.objective = new ObjectiveService(new ObjectiveApi(this.client, '/objective'), this.config);
        // this.pageApprovalConfig / this.pendingApproval / this.pendingChange removed (Plan Faz 1.7).
        this.partyTemplate = new PartyTemplateService(new PartyTemplateApi(this.client), this.config);
        this.partyInstance = new PartyInstanceService(new PartyInstanceApi(this.client), this.config);
        this.workflowTemplate = new WorkflowTemplateService(new WorkflowTemplateApi(this.client), this.config);
        this.workflowInstance = new WorkflowInstanceService(new WorkflowInstanceApi(this.client), this.config);
        this.workflowTask = new WorkflowTaskService(new WorkflowTaskApi(this.client), this.config);
        this.businessCalendar = new BusinessCalendarService(new BusinessCalendarApi(this.client), this.config);
        this.entityEvent = new EntityEventService(new EntityEventApi(this.client), this.config);
        this.delegation = new DelegationService(new DelegationApi(this.client));
        this.deadLetter = new DeadLetterService(new DeadLetterApi(this.client));
        this.backup = new BackupService(new BackupApi(this.client));
        this.tailorability = new TailorabilityService(new TailorabilityApi(this.client));
        this.webhookSubscription = new WebhookSubscriptionService(new WebhookSubscriptionApi(this.client));
        this.tenantCompliance = new TenantComplianceService(new TenantComplianceApi(this.client));
        this.integrationExport = new IntegrationExportService(new IntegrationExportApi(this.client));
        this.customerImport = new CustomerImportService(new CustomerImportApi(this.client));
        this.tenantOnboarding = new TenantOnboardingService(new TenantOnboardingApi(this.client));
        this.myWork = new MyWorkService(new MyWorkApi(this.client), this.config);
        this.personalDataCategory = new PersonalDataCategoryService(new PersonalDataCategoryApi(this.client, '/personal-data-category'), this.config);
        this.personalDataInventory = new PersonalDataInventoryService(new PersonalDataInventoryApi(this.client, '/personal-data-inventory'), this.config);
        this.privacyPrinciple = new PrivacyPrincipleService(new PrivacyPrincipleApi(this.client, '/privacy-principle'), this.config);
        this.process = new ProcessService(new ProcessApi(this.client, '/process'), this.config);
        this.processingPurpose = new ProcessingPurposeService(new ProcessingPurposeApi(this.client, '/processing-purpose'), this.config);
        this.queueObservability = new QueueObservabilityService(new QueueObservabilityApi(this.client), this.config);
        this.outboxMonitor = new OutboxMonitorService(new OutboxMonitorApi(this.client), this.config);
        this.eventObservability = new EventObservabilityService(new EventObservabilityApi(this.client), this.config);
        this.recipientGroup = new RecipientGroupService(new RecipientGroupApi(this.client, '/recipient-group'), this.config);
        this.recoveryResources = new RecoveryResourcesService(new RecoveryResourcesApi(this.client, '/recovery-resources'), this.config);
        this.lessonLearned = new LessonLearnedService(new LessonLearnedApi(this.client, '/lesson-learned'), this.config);
        this.reportBranding = new ReportBrandingService(new ReportBrandingApi(this.client));
        this.reportArtifacts = new ReportArtifactService(new ReportArtifactApi(this.client));
        this.reportProfiles = new ReportProfileService(new ReportProfileApi(this.client));
        // Compatibility client for old scheduled-job/profile readers during the migration window.
        this.detailedReportProfiles = new DetailedReportProfileService(new DetailedReportProfileApi(this.client));
        this.reportConfidentialityClasses = new SimpleCrudService(
            new BaseApi(this.client, '/report-confidentiality-classes'),
            { listMessage: 'Rapor gizlilik sınıfları getirildi' },
        );
        this.detailedReporting = new DetailedReportingService(new DetailedReportingApi(this.client));
        this.reportSurfaces = new ReportSurfacesService(new ReportSurfacesApi(this.client));
        this.reportRuns = new ReportRunsService(new ReportRunsApi(this.client));
        this.retentionPeriod = new RetentionPeriodService(new RetentionPeriodApi(this.client, '/retention-period'), this.config);
        this.riskAnalysis = new RiskAnalysisService(new RiskAnalysisApi(this.client, '/risk-analysis'), this.config);
        this.riskRegister = new RiskRegisterService(new RiskRegisterApi(this.client, '/risk-register'), this.config);
        this.riskConsequence = new RiskConsequenceService(new RiskConsequenceApi(this.client), this.config);
        this.riskReports = new RiskReportsService(new RiskReportsApi(this.client, '/risk-reports'), this.config);
        this.riskTreatment = new RiskTreatmentService(new RiskTreatmentApi(this.client, '/risk-treatment'), this.config);
        this.risk = new RiskService(new RiskApi(this.client, '/risk'), this.config);
        this.role = new RoleService(new RoleApi(this.client, '/role'), this.config);
        this.securityMeasure = new SecurityMeasureService(new SecurityMeasureApi(this.client, '/security-measure'), this.config);
        this.strategicInitiative = new StrategicInitiativeService(new StrategicInitiativeApi(this.client, '/strategic-initiative'), this.config);
        this.subjectPersonGroup = new SubjectPersonGroupService(new SubjectPersonGroupApi(this.client, '/subject-person-group'), this.config);
        this.systemLogs = new SystemLogsService(new SystemLogsApi(this.client, '/system-logs'), this.config);
        this.evidenceBundles = new EvidenceBundleService(new EvidenceBundleApi(this.client));
        this.accessLogs = new AccessLogsService(new AccessLogsApi(this.client, '/access-logs'), this.config);
        this.team = new TeamService(new TeamApi(this.client, '/team'), this.config);
        this.dutyDefinition = new SimpleCrudService(new BaseApi(this.client, '/duty-definition'), {
            listMessage: 'R&R tanımları getirildi',
        });
        this.tenantDirectory = new TenantDirectoryService(new TenantDirectoryApi(this.client), this.config);
        this.identityProvider = new IdentityProviderService(new IdentityProviderApi(this.client));
        this.threat = new ThreatService(new ThreatApi(this.client, '/threat'), this.config);
        this.training = new TrainingService(new TrainingApi(this.client, '/training'), this.config);
        this.transferType = new TransferTypeService(new TransferTypeApi(this.client, '/transfer-type'), this.config);
        this.internationalTransfer = new InternationalTransferService(new InternationalTransferApi(this.client, '/international-transfer'), this.config);
        this.userTraining = new UserTrainingService(new UserTrainingApi(this.client, '/user-training'), this.config);
        this.user = new UserService(new UserApi(this.client, '/user'), this.config);
        this.vendor = new VendorService(new VendorApi(this.client, '/vendor'), this.config);
        this.weakness = new WeaknessService(new WeaknessApi(this.client, '/weakness'), this.config);
        this.referenceData = new ReferenceDataService(new ReferenceDataApi(this.client), this.config);
        this.rpoCalculation = new RpoCalculationService(new RpoCalculationApi(this.client, '/rpo-calculation'), this.config);
        this.rpoRecommendation = new RpoRecommendationService(new RpoRecommendationApi(this.client, '/rpo-recommendation'), this.config);

        this.regulatorySource = new SimpleCrudService(new RegulatorySourceApi(this.client), { listMessage: 'Düzenleyici kaynaklar getirildi' });
        this.regulatoryClause = new SimpleCrudService(new RegulatoryClauseApi(this.client), { listMessage: 'Maddeler getirildi' });
        this.complianceObligation = new SimpleCrudService(new ComplianceObligationApi(this.client), { listMessage: 'Yükümlülükler getirildi' });
        this.complianceFramework = new SimpleCrudService(new ComplianceFrameworkApi(this.client), { listMessage: 'Uyumluluk çerçeveleri getirildi' });
        this.riskMitigationDefinition = new SimpleCrudService(new RiskMitigationDefinitionApi(this.client), { listMessage: 'Azaltıcı önlemler getirildi' });
        this.dpiaTemplate = new SimpleCrudService(new DpiaTemplateApi(this.client), { listMessage: 'DPIA şablonları getirildi' });
        this.incidentTemplate = new SimpleCrudService(new IncidentTemplateApi(this.client), { listMessage: 'Olay şablonları getirildi' });
        this.vendorAssessment = new SimpleCrudService(new VendorAssessmentApi(this.client), { listMessage: 'Tedarikçi değerlendirmeleri getirildi' });
        this.integrationConnector = new SimpleCrudService(new IntegrationConnectorApi(this.client), { listMessage: 'Bağlayıcılar getirildi' });
        this.governanceReportDefinition = new SimpleCrudService(new GovernanceReportDefinitionApi(this.client), { listMessage: 'Rapor tanımları getirildi' });
        // this.automationDefinition removed (Plan Faz 1.7). Replaced by WorkflowTemplate (Plan Faz 5, ADR-006).
    }

    /**
     * Standard GET with optional schema validation
     */
    async get(url, config = {}) {
        const response = await this.client.get(url, config);
        return response.data;
    }

    async post(url, data, config = {}) {
        const response = await this.client.post(url, data, config);
        return response.data;
    }

    async put(url, data, config = {}) {
        const response = await this.client.put(url, data, config);
        return response.data;
    }

    async delete(url, config = {}) {
        const response = await this.client.delete(url, config);
        return response.data;
    }

    // --- Private Helpers ---

    _getDefaultTokenStore() {
        return {
            get: () => typeof window !== 'undefined' ? localStorage.getItem('token') : null,
            getRefreshToken: () => typeof window !== 'undefined' ? localStorage.getItem('refreshToken') : null,
            set: (token) => typeof window !== 'undefined' ? localStorage.setItem('token', token) : null,
            setTokens: ({ accessToken, refreshToken }) => {
                if (typeof window !== 'undefined') {
                    localStorage.setItem('token', accessToken);
                    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
                }
            },
            clear: () => {
                if (typeof window !== 'undefined') {
                    localStorage.removeItem('token');
                    localStorage.removeItem('refreshToken');
                }
            }
        };
    }

    async _handleRefresh() {
        if (this._refreshPromise) return this._refreshPromise;

        this._refreshPromise = (async () => {
            if (typeof this.tokenStore.shouldSkipAuthRefresh === 'function' && this.tokenStore.shouldSkipAuthRefresh()) {
                return null;
            }
            console.warn('[SDK] Token expired. Attempting refresh...');
            try {
                const refreshToken = this.tokenStore.getRefreshToken
                    ? await this.tokenStore.getRefreshToken()
                    : null;

                if (!refreshToken) {
                    throw new Error('No refresh token available');
                }

                const headers = { 'Content-Type': 'application/json' };

                // Try to get tenantId from config or store if available to maintain context
                let tenantId = this.config.tenantId;
                if (!tenantId && this.tokenStore.getTenantId) {
                    tenantId = await this.tokenStore.getTenantId();
                }
                if (tenantId) {
                    headers['X-Tenant-Id'] = tenantId;
                }

                const response = await fetch(`${this.config.baseURL}/auth/refresh`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ refreshToken })
                });

                if (!response.ok) {
                    throw new Error('Refresh failed');
                }

                const data = await response.json();
                const newToken = data.data?.token || data.token;
                const newRefreshToken = data.data?.refreshToken || data.refreshToken;

                if (newToken) {
                    if (this.tokenStore.setTokens) {
                        this.tokenStore.setTokens({
                            accessToken: newToken,
                            refreshToken: newRefreshToken || refreshToken
                        });
                    } else {
                        this.tokenStore.set(newToken);
                    }

                    return newToken;
                }
                throw new Error('No token in refresh response');
            } catch (error) {
                console.error('[SDK] Refresh failed', error);
                this.tokenStore.clear();
                if (typeof window !== 'undefined') {
                    if (!window.location.pathname.includes('login')) {
                        window.location.href = '/login';
                    }
                }
                return null;
            } finally {
                this._refreshPromise = null;
            }
        })();

        return this._refreshPromise;
    }
}

export const createSDK = (config) => new APIClient(config);
export { isUploadAbortError } from './utils/upload-axios-config.js';
export {
    ApiError,
    BaseApi,
    ReportBrandingApi,
    ReportBrandingService,
    ReportArtifactApi,
    ReportArtifactService,
    ReportProfileApi,
    ReportProfileService,
    EvidenceBundleApi,
    EvidenceBundleService,
    NotificationRoutingApi,
    NotificationRoutingService,
    OperationalRunApi,
    OperationalRunService,
};
export * from './types/auth.types.js';
export * from './types/asset.types.js';
export * from './types/unit.types.js';
export * from './types/threat.types.js';
export * from './types/user.types.js';
export * from './types/weakness.types.js';
